import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { IngestedOrder } from '@model/ingested-order/ingested-order.model';
import { FilterOptionsResponse } from '@model/filter/filter-options.model';
import {
  IngestedOrdersQueryParams,
  OrderStatus
} from '../api/ingested-orders-api.interface';
import { OrdersService } from '../../orders/orders.service';

/**
 * Service for managing ingested orders (status=ADMITTED)
 * Wrapper around OrdersService with default status=ADMITTED
 */
@Injectable({
  providedIn: 'root'
})
export class IngestedOrdersService {
  private static readonly DEFAULT_STATUS = OrderStatus.INGRESADA;
  private readonly ordersService = inject(OrdersService);

  /**
   * Get paginated list of ingested orders (status=ADMITTED)
   * Delegates to OrdersService with INGRESADA status
   */
  getOrders(params?: IngestedOrdersQueryParams): Observable<{
    orders: IngestedOrder[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.ordersService.getOrders({
      ...params,
      status: IngestedOrdersService.DEFAULT_STATUS
    }) as Observable<{
      orders: IngestedOrder[];
      pagination: {
        page: number;
        size: number;
        total: number;
        totalPages: number;
      };
    }>;
  }

  /**
   * Fetches available filter options for ingested orders
   * Combines three separate API calls:
   * - GET /preanalytical/master-data/regionals
   * - GET /preanalytical/master-data/sites
   * - GET /preanalytical/master-data/exam-types
   */
  getFilterOptions(): Observable<FilterOptionsResponse> {
    return forkJoin({
      regions: this.ordersService.getRegions(),
      sites: this.ordersService.getSites(),
      examTypes: this.ordersService.getExamTypes()
    }).pipe(
      map((response) => ({
        regions: response.regions,
        sites: response.sites,
        examTypes: response.examTypes
      }))
    );
  }

  /**
   * GET /preanalitica/admitted-orders/export?status=ADMITTED
   * Export ingested orders to CSV file
   */
  exportOrders(params?: IngestedOrdersQueryParams): Observable<Blob> {
    return this.ordersService.exportOrders({
      ...params,
      status: IngestedOrdersService.DEFAULT_STATUS
    });
  }
}
